import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import {
  memo,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'

export type TooltipClassNames = {
  trigger?: string
  positioner?: string
  popup?: string
  arrow?: string
}

export type TooltipProps = {
  children: ReactElement
  label?: ReactNode
  onlyOnOverflow?: boolean
  classNames?: TooltipClassNames
  portalToRoot?: boolean
}

export const TooltipProvider = BaseTooltip.Provider

export const Tooltip = memo(function Tooltip({
  children,
  label,
  onlyOnOverflow = false,
  classNames,
  portalToRoot = false,
}: TooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null) // Doesn't actually render a button, but needed for types in BaseUI
  const hasOverflow = useHasOverflow(triggerRef, onlyOnOverflow)
  const [open, setOpen] = useState(false)

  if (!label) return children

  return (
    <BaseTooltip.Root
      {...(onlyOnOverflow && {
        open: open && hasOverflow,
        onOpenChange: setOpen,
      })}
    >
      <BaseTooltip.Trigger
        className={classNames?.trigger}
        render={children}
        ref={triggerRef}
      />
      <BaseTooltip.Portal
        container={portalToRoot ? document.getElementById('root') : undefined}
      >
        <BaseTooltip.Positioner
          sideOffset={8}
          className={classNames?.positioner}
          style={{ zIndex: 9999 }}
        >
          <BaseTooltip.Popup className={classNames?.popup}>
            {label}
            {classNames?.arrow !== undefined && (
              <BaseTooltip.Arrow className={classNames?.arrow} />
            )}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
})

function useHasOverflow(ref: RefObject<Element | null>, enabled: boolean) {
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    if (!enabled || !ref.current) return
    const { clientWidth = 0, scrollWidth = 0 } = ref.current
    setHasOverflow(scrollWidth > clientWidth)
  }, [ref, enabled])

  return hasOverflow
}
